import { useEffect, useContext, useState } from 'react';
import type { NodeToRenderProps, CustomGenericNodeData } from '@src/components/custom-nodes/types';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor';
import {
  reactFlowInstanceAtom,
  getOpenedInfoModalCounter,
} from '@src/atoms';

import { ajvResolver } from "@hookform/resolvers/ajv"
import { useForm, Controller } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { updateNode } from '@src/scenario-editor/utils';
import { validatorInstance } from '@src/scenario-editor/validator/Validator';
import { ExclamationTriangleIcon, LockClosedIcon } from '@radix-ui/react-icons';
import { CustomTooltip } from '@src/components/tooltip/Tooltip';
import { getComponentByFieldType } from '@src/components/node-info/utils/getComponentByFieldType.tsx';
import {JSONSchemaType} from 'ajv';

// LF360-1823 (#118): text-red-500 - сирий Tailwind-колір; --destructive у темі має те саме
// значення в світлій темі (hsl(0 84.2% 60.2%) === #EF4444), тож візуально це no-op, але
// іконка починає адаптуватися до темної теми. size-4 - ті самі 16px, що й решта іконок.
interface GenericNodeInfoProps {
  selectedNodeProps: NodeToRenderProps;
  isPreview: boolean;
}

export const GenericNodeInfo = ({ selectedNodeProps, isPreview }: GenericNodeInfoProps) => {
  const selectedNodePropsData = selectedNodeProps?.data as CustomGenericNodeData;
  const [alternativesState, setAlternativesState] = useState<Record<string, unknown>>({});

  const FormSchema = selectedNodePropsData.validationSchema!;
  const FormSchemaFromValidator = validatorInstance.getSchema(selectedNodePropsData.nodeType ?? '') as JSONSchemaType<unknown>;

  const form = useForm<Record<string, unknown>>({
    resolver: ajvResolver(FormSchemaFromValidator ?? FormSchema),
    defaultValues: selectedNodePropsData.nodeInfoContent.reduce((acc, { fieldName, defaultValue }) => {
      acc[fieldName] = selectedNodePropsData[fieldName as keyof typeof selectedNodePropsData] ?? defaultValue;
      return acc;
    }, {} as Record<string, unknown>)
  });

  const reactFlowInstance = useContext(ReactflowInstanceContext);
  const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;

  const updateItem = updateNode({ reactFlowInstance: instance });

  useEffect(() => {
    // initial form trigger
    setTimeout(() => {
      void form.trigger();
    }, 500)
    // }
    changeHandler(formValues);
  }, []);

  useEffect(() => {
    let prevFormValues = form.getValues();
    const subscription = form.watch(() => {
      // INFO: track which field was changed
      const fieldName = Object.keys(form.getValues()).find((key) => form.getValues()[key] !== prevFormValues[key]);
      prevFormValues = form.getValues();
      const fieldsToBeTrackedOnBlur = ['timeout', 'message'];

      if (!fieldName || !fieldsToBeTrackedOnBlur.includes(fieldName ?? '')) {
        void form.handleSubmit(changeHandler)()
        changeHandler(form.getValues());
      }
    });

    return () => subscription.unsubscribe();
  }, [form.handleSubmit, form.watch]);

  const changeHandler = (data: Record<string, unknown>) => {
    updateItem?.(selectedNodeProps?.id ?? '', data);
  }

  const formValues = form.getValues();
  const shouldShowErrorWarnings = getOpenedInfoModalCounter(selectedNodeProps.id) > 1;
  // INFO: externally-created nodes are backend-managed: every field is locked
  // EXCEPT the always-editable ID field below (it's the mapping handle the
  // scenario author needs to keep editable). `lockFields` adds granular,
  // per-field locks on top - locking a named subset even when the node is not
  // externally-created. The actual per-field decision is `isFieldLocked` in
  // the map() below.
  const ALWAYS_UNLOCKED_FIELDS = ['questionId'];
  const isNodeLocked = selectedNodePropsData.externallyCreated === true;
  const lockedFieldNames = selectedNodePropsData.lockFields ?? [];

  return (
    <Form {...form}>
      <div className="grid grid-cols-[150px_minmax(200px,_1fr)] text-sm p-4 max-h-[70vh] overflow-y-auto overflow-x-hidden">
        {selectedNodePropsData.nodeInfoContent.map(({
          label,
          fieldName,
          fieldType,
          placeholder,
          options,
          predicate,
          disabled,
          showGenericValidation = true,
        }) => {
          // INFO: the ID field stays editable on locked nodes; any other field
          // locks when the whole node is externally-managed OR it is named in
          // `lockFields`.
          const isFieldLocked =
            !ALWAYS_UNLOCKED_FIELDS.includes(fieldName) &&
            (isNodeLocked || lockedFieldNames.includes(fieldName));
          return (
            (predicate && predicate.every(({ key, value }: { key: string, value: string }) => formValues[key] === value)) ?? !predicate) ? (
            <>
              <div key={fieldName} className="py-2 pt-5 text-label-foreground">{label}</div>
              <div key={`${fieldName}-controller`} className="pl-2 py-2 pt-4">
                <Controller
                  control={form.control}
                  name={fieldName as 'label'}
                  render={({ field, fieldState }) => {
                    const Component = getComponentByFieldType(fieldType);
                    const tooltipItemMessage =
                      fieldState?.error?.message
                      ?? Object.keys(fieldState?.error ?? {}).map((key) => ((fieldState?.error as Record<string, unknown>)?.[key] as { message: string })?.message).join(', ')
                      ?? '';

                    return (
                      <div className="relative" data-testid={`info=>field:${fieldName}`}>
                        <Component {...{
                          value: field.value as never,
                          alternatives: alternativesState[`${fieldName}_alternatives`] ?? selectedNodePropsData?.[`${fieldName}_alternatives` as never],
                          onValueChange: (value: string, extraOpts?: Record<string, unknown>) => {
                            field.onChange(value);
                            // INFO: Store alternative values if exist
                            if (extraOpts && Object.keys(extraOpts).includes('alternatives')) {
                              setTimeout(() => {
                                updateItem?.(selectedNodeProps?.id ?? '', {
                                  [`${fieldName}_alternatives`]: extraOpts?.alternatives,
                                });
                                setAlternativesState({
                                  ...alternativesState,
                                  [`${fieldName}_alternatives`]: extraOpts?.alternatives,
                                });
                              }, 100);
                            }
                          },
                          onBlur: () => {
                            const formData = form.getValues();
                            const updatedPayload = {
                              ...formData,
                              [fieldName]: formData[fieldName],
                            };
                            changeHandler(updatedPayload);
                            void form.handleSubmit(() => changeHandler(updatedPayload))()
                          },

                          validationErrors: fieldState.error as unknown,
                          options,
                          disabled: isFieldLocked || (disabled ?? isPreview),
                          isPreview,
                          placeholder,
                        }} />
                        {isFieldLocked ? (
                          // INFO: locked field shows a neutral lock icon in the
                          // gutter; the validation triangle is suppressed because
                          // an error on a read-only field is unactionable.
                          <div className="absolute -left-[1.3rem] top-[0.5rem]">
                            <CustomTooltip content="Managed externally - read only">
                              <LockClosedIcon className="text-muted-foreground" />
                            </CustomTooltip>
                          </div>
                        ) : (
                          shouldShowErrorWarnings
                          && showGenericValidation
                          && fieldState.error
                          && (
                            <div className="absolute -left-[1.3rem] top-[0.5rem]">
                              <CustomTooltip
                                content={tooltipItemMessage}
                              >
                                <ExclamationTriangleIcon className="size-4 text-destructive"/>
                              </CustomTooltip>
                            </div>
                          )
                        )}
                      </div>
                    );
                  }}
                >
                </Controller>
              </div>
            </>
          ) : null;
        })}
      </div>
    </Form>
  );
}
